import React, { useState, useEffect, useCallback } from 'react';
import {
  Users,
  Building2,
  Gift,
  HandCoins,
  TrendingDown,
  AlertTriangle,
  RefreshCw,
  Database,
  TrendingUp,
} from 'lucide-react';
import DataTable from './DataTable';
import DeleteConfirmation from './DeleteConfirmation';
import EstablishmentCard from './EstablishmentCard';
import { employeeAPI, establishmentAPI, wagesAPI } from '../api/apiService';
import { notifySavedAndReload } from '../utils/notify';
import './AdminDashboard.css';

// Category configuration with all metadata
const CATEGORIES = {
  employees: {
    id: 'employees',
    label: 'Employees',
    icon: Users,
    color: '#4338ca',
    bgColor: 'rgba(67,56,202,0.07)',
    borderColor: 'rgba(67,56,202,0.18)',
    api: {
      fetch: (estId) => employeeAPI.getByEstablishment(estId),
      delete: (id) => employeeAPI.delete(id),
      responseKey: 'employees',
    },
    columns: [
      { key: 'employee_id', label: 'Employee ID', width: '120px' },
      {
        key: 'first_name',
        label: 'Name',
        width: '200px',
        render: (value, row) => `${row.first_name || ''} ${row.last_name || ''}`.trim() || '-',
      },
      { key: 'email', label: 'Email', width: '220px' },
      { key: 'department', label: 'Department', width: '150px' },
      { key: 'position', label: 'Position', width: '150px' },
    ],
    getFilters: (data) => {
      const departments = [...new Set(data.map((e) => e.department).filter(Boolean))].sort();
      const positions = [...new Set(data.map((e) => e.position).filter(Boolean))].sort();
      return [
        { key: 'department', label: 'Department', options: departments.map((d) => ({ value: d, label: d })) },
        { key: 'position', label: 'Position', options: positions.map((p) => ({ value: p, label: p })) },
      ];
    },
    getItemName: (item) => `${item.first_name} ${item.last_name} (${item.employee_id})`,
    getItemId: (item) => item.employee_id,
    itemType: 'employee',
    searchPlaceholder: 'Search by ID, name, email, department...',
  },
  bonuses: {
    id: 'bonuses',
    label: 'Bonuses',
    icon: Gift,
    color: '#15803d',
    bgColor: 'rgba(21,128,61,0.07)',
    borderColor: 'rgba(21,128,61,0.18)',
    api: {
      fetch: (estId) => wagesAPI.getBonuses({ establishment_id: estId }),
      delete: (id) => wagesAPI.deleteBonus(id),
      responseKey: 'bonusAllowances',
    },
    columns: [
      { key: 'id', label: 'ID', width: '70px' },
      {
        key: 'employee_name',
        label: 'Employee/Department',
        width: '180px',
        render: (v, row) => v || row.department || row.employee_id || '-'
      },
      { key: 'bonus_type', label: 'Type', width: '150px' },
      {
        key: 'amount',
        label: 'Amount',
        width: '120px',
        render: (value, row) => {
          if (row.calculation_type === 'percentage' && row.bonus_percentage) {
            return `${row.bonus_percentage}%`;
          }
          return value ? `₹${parseFloat(value).toLocaleString('en-IN', { minimumFractionDigits: 2 })}` : '-';
        },
      },
      { key: 'effective_date', label: 'Date', width: '120px' },
      { key: 'payment_month', label: 'Month', width: '100px' },
    ],
    getFilters: (data) => {
      const types = [...new Set(data.map((b) => b.bonus_type).filter(Boolean))].sort();
      const depts = [...new Set(data.map((b) => b.department).filter(Boolean))].sort();
      const months = [...new Set(data.map((b) => b.payment_month).filter(Boolean))].sort();
      return [
        { key: 'bonus_type', label: 'Bonus Type', options: types.map((t) => ({ value: t, label: t })) },
        { key: 'department', label: 'Department', options: depts.map((d) => ({ value: d, label: d })) },
        { key: 'payment_month', label: 'Month', options: months.map((m) => ({ value: m, label: m })) },
      ];
    },
    getItemName: (item) => `Bonus #${item.id} - ${item.bonus_type}`,
    getItemId: (item) => item.id,
    itemType: 'bonus/allowance',
    searchPlaceholder: 'Search bonuses...',
  },
  loans: {
    id: 'loans',
    label: 'Loans',
    icon: HandCoins,
    color: '#b45309',
    bgColor: 'rgba(180,83,9,0.07)',
    borderColor: 'rgba(180,83,9,0.18)',
    api: {
      fetch: (estId) => wagesAPI.getLoans({ establishment_id: estId }),
      delete: (id) => wagesAPI.deleteLoan(id),
      responseKey: 'loansAdvances',
    },
    columns: [
      { key: 'id', label: 'ID', width: '70px' },
      {
        key: 'employee_name',
        label: 'Employee',
        width: '180px',
        render: (v, row) => v || row.department || row.employee_id || '-'
      },
      { key: 'loan_type', label: 'Type', width: '150px' },
      {
        key: 'amount',
        label: 'Amount',
        width: '120px',
        render: (value) => (value ? `₹${parseFloat(value).toLocaleString('en-IN', { minimumFractionDigits: 2 })}` : '-'),
      },
      { key: 'installments', label: 'Installments', width: '110px' },
      { key: 'issue_date', label: 'Issue Date', width: '120px' },
      {
        key: 'status',
        label: 'Status',
        width: '100px',
        render: (value) => (
          <span className={`admin-status-badge admin-status-${(value || 'pending').toLowerCase()}`}>
            {value || 'Pending'}
          </span>
        ),
      },
    ],
    getFilters: (data) => {
      const types = [...new Set(data.map((l) => l.loan_type).filter(Boolean))].sort();
      const statuses = [...new Set(data.map((l) => l.status).filter(Boolean))].sort();
      return [
        { key: 'loan_type', label: 'Loan Type', options: types.map((t) => ({ value: t, label: t })) },
        { key: 'status', label: 'Status', options: statuses.map((s) => ({ value: s, label: s })) },
      ];
    },
    getItemName: (item) => `Loan #${item.id} - ${item.loan_type}`,
    getItemId: (item) => item.id,
    itemType: 'loan/advance',
    searchPlaceholder: 'Search loans...',
  },
  recoveries: {
    id: 'recoveries',
    label: 'Recoveries',
    icon: TrendingDown,
    color: '#6d28d9',
    bgColor: 'rgba(109,40,217,0.07)',
    borderColor: 'rgba(109,40,217,0.18)',
    api: {
      fetch: (estId) => wagesAPI.getRecoveries({ establishment_id: estId }),
      delete: (id) => wagesAPI.deleteRecovery(id),
      responseKey: 'recoveries',
    },
    columns: [
      { key: 'id', label: 'ID', width: '70px' },
      {
        key: 'employee_name',
        label: 'Employee',
        width: '180px',
        render: (v, row) => v || row.department || row.employee_id || '-'
      },
      { key: 'recovery_type', label: 'Recovery Type', width: '150px' },
      { key: 'deduction_type', label: 'Deduction Type', width: '150px' },
      {
        key: 'amount',
        label: 'Amount',
        width: '120px',
        render: (v) => (v ? `₹${parseFloat(v).toLocaleString('en-IN', { minimumFractionDigits: 2 })}` : '-'),
      },
      { key: 'recovery_date', label: 'Date', width: '120px' },
      { key: 'reason', label: 'Reason', width: '200px' },
    ],
    getFilters: (data) => {
      const recoveryTypes = [...new Set(data.map((r) => r.recovery_type).filter(Boolean))].sort();
      const deductionTypes = [...new Set(data.map((r) => r.deduction_type).filter(Boolean))].sort();
      return [
        { key: 'recovery_type', label: 'Recovery Type', options: recoveryTypes.map((t) => ({ value: t, label: t })) },
        { key: 'deduction_type', label: 'Deduction Type', options: deductionTypes.map((t) => ({ value: t, label: t })) },
      ];
    },
    getItemName: (item) => `Recovery #${item.id}`,
    getItemId: (item) => item.id,
    itemType: 'recovery',
    searchPlaceholder: 'Search recoveries...',
  },
  damages: {
    id: 'damages',
    label: 'Loss & Damages',
    icon: AlertTriangle,
    color: '#b91c1c',
    bgColor: 'rgba(185,28,28,0.07)',
    borderColor: 'rgba(185,28,28,0.18)',
    api: {
      fetch: (estId) => wagesAPI.getDamages({ establishment_id: estId }),
      delete: (id) => wagesAPI.deleteDamage(id),
      responseKey: 'damages',
    },
    columns: [
      { key: 'id', label: 'ID', width: '70px' },
      {
        key: 'employee_name',
        label: 'Employee',
        width: '180px',
        render: (v, row) => v || row.employee_id || '-'
      },
      { key: 'damage_type', label: 'Damage Type', width: '150px' },
      {
        key: 'amount',
        label: 'Amount',
        width: '120px',
        render: (v) => (v ? `₹${parseFloat(v).toLocaleString('en-IN', { minimumFractionDigits: 2 })}` : '-'),
      },
      { key: 'severity', label: 'Severity', width: '100px',
        render: (v) => (
          <span className={`admin-severity-badge admin-severity-${(v || 'minor').toLowerCase()}`}>
            {v || 'Minor'}
          </span>
        ),
      },
      { key: 'incident_date', label: 'Incident Date', width: '120px' },
      { key: 'recovery_method', label: 'Recovery Method', width: '130px' },
    ],
    getFilters: (data) => {
      const types = [...new Set(data.map((d) => d.damage_type).filter(Boolean))].sort();
      const severities = [...new Set(data.map((d) => d.severity).filter(Boolean))].sort();
      return [
        { key: 'damage_type', label: 'Damage Type', options: types.map((t) => ({ value: t, label: t })) },
        { key: 'severity', label: 'Severity', options: severities.map((s) => ({ value: s, label: s })) },
      ];
    },
    getItemName: (item) => `Damage #${item.id} - ${item.damage_type}`,
    getItemId: (item) => item.id,
    itemType: 'loss/damage',
    searchPlaceholder: 'Search loss & damages...',
  },
};

const AdminDashboard = () => {
  const [establishments, setEstablishments] = useState([]);
  const [selectedEstablishment, setSelectedEstablishment] = useState('');
  const [loadingEstablishments, setLoadingEstablishments] = useState(true);
  const [activeCategory, setActiveCategory] = useState('employees');
  const [data, setData] = useState({});
  const [loading, setLoading] = useState({});
  const [stats, setStats] = useState({});
  const [deleteModal, setDeleteModal] = useState({ isOpen: false, item: null });
  const [deleting, setDeleting] = useState(false);

  const category = CATEGORIES[activeCategory];

  // Fetch establishments on mount
  useEffect(() => {
    const fetchEstablishments = async () => {
      try {
        setLoadingEstablishments(true);
        const response = await establishmentAPI.getAll();
        const ests = response.establishments || [];
        setEstablishments(ests);
        // Auto-select first establishment if available
        if (ests.length > 0) {
          setSelectedEstablishment(ests[0].id.toString());
        }
      } catch (error) {
        console.error('Error fetching establishments:', error);
      } finally {
        setLoadingEstablishments(false);
      }
    };
    fetchEstablishments();
  }, []);

  // Fetch data for a specific category
  const fetchCategoryData = useCallback(async (categoryId, establishmentId, showLoading = true) => {
    if (!establishmentId) return;

    const cat = CATEGORIES[categoryId];
    if (!cat) return;

    try {
      if (showLoading) {
        setLoading((prev) => ({ ...prev, [categoryId]: true }));
      }
      const response = await cat.api.fetch(establishmentId);
      const items = response[cat.api.responseKey] || [];
      setData((prev) => ({ ...prev, [categoryId]: items }));
      setStats((prev) => ({ ...prev, [categoryId]: items.length }));
    } catch (error) {
      console.error(`Error fetching ${categoryId}:`, error);
      setData((prev) => ({ ...prev, [categoryId]: [] }));
      setStats((prev) => ({ ...prev, [categoryId]: 0 }));
    } finally {
      setLoading((prev) => ({ ...prev, [categoryId]: false }));
    }
  }, []);

  // Fetch all category data when organization changes
  useEffect(() => {
    if (selectedEstablishment) {
      // Reset data and stats
      setData({});
      setStats({});
      // Fetch all categories for stats
      Object.keys(CATEGORIES).forEach((categoryId) => {
        fetchCategoryData(categoryId, selectedEstablishment, categoryId === activeCategory);
      });
    }
  }, [selectedEstablishment, fetchCategoryData, activeCategory]);

  const handleEstablishmentChange = (e) => {
    setSelectedEstablishment(e.target.value);
  };

  const handleRefresh = () => {
    if (selectedEstablishment) {
      fetchCategoryData(activeCategory, selectedEstablishment);
    }
  };

  const handleDeleteClick = (item) => {
    setDeleteModal({ isOpen: true, item });
  };

  const handleDeleteConfirm = async () => {
    if (!deleteModal.item) return;

    try {
      setDeleting(true);
      const itemId = category.getItemId(deleteModal.item);
      const response = await category.api.delete(itemId);

      // Show success message with archived info if available
      if (response.archived) {
        alert(
          `${response.message}\n\nArchived Records:\n` +
            `- Bonuses: ${response.archived.bonuses}\n` +
            `- Loans: ${response.archived.loans}\n` +
            `- Recoveries: ${response.archived.recoveries}\n` +
            `- Variable Pay: ${response.archived.variable_pay}`
        );
      } else {
        notifySavedAndReload(response.message || 'Record deleted successfully');
      }

      setDeleteModal({ isOpen: false, item: null });
      fetchCategoryData(activeCategory, selectedEstablishment);
    } catch (error) {
      console.error('Error deleting:', error);
      alert('Error: ' + error.message);
    } finally {
      setDeleting(false);
    }
  };

  const currentData = data[activeCategory] || [];
  const currentLoading = loading[activeCategory] || false;
  const filters = category.getFilters(currentData);

  // Calculate total records across all categories
  const totalRecords = Object.values(stats).reduce((sum, count) => sum + (count || 0), 0);

  // Get selected establishment name
  const selectedEstablishmentName = establishments.find(e => e.id.toString() === selectedEstablishment)?.name || '';

  return (
    <div className="admin-dashboard">
      {/* Header Section */}
      <div className="admin-header">
        <div className="admin-header-content">
          <div className="admin-header-title-section">
            <div className="admin-header-icon">
              <Database size={20} />
            </div>
            <div>
              <h1 className="admin-header-title">Admin Center</h1>
              <p className="admin-header-subtitle">Manage and monitor all organizational records</p>
            </div>
          </div>
          {selectedEstablishment && (
            <div className="admin-total-badge">
              <TrendingUp size={13} />
              <span>{totalRecords.toLocaleString()} Total Records</span>
            </div>
          )}
        </div>
      </div>

      {/* Establishment Selector */}
      <EstablishmentCard
        establishments={establishments}
        value={selectedEstablishment}
        onChange={handleEstablishmentChange}
        loading={loadingEstablishments}
      />

      {/* Show content only when establishment is selected */}
      {selectedEstablishment ? (
        <>
          {/* Category Stats Cards */}
          <div className="admin-stats-grid">
            {Object.values(CATEGORIES).map((cat) => {
              const Icon = cat.icon;
              const isActive = activeCategory === cat.id;
              const count = stats[cat.id] || 0;

              return (
                <button
                  key={cat.id}
                  className={`admin-stat-card ${isActive ? 'active' : ''}`}
                  onClick={() => setActiveCategory(cat.id)}
                  style={{
                    '--card-color': cat.color,
                    '--card-bg': cat.bgColor,
                    '--card-border': cat.borderColor,
                    '--card-bg-hover': cat.bgColor.replace('0.07', '0.12'),
                  }}
                >
                  <div className="admin-stat-icon" style={{ color: cat.color }}>
                    <Icon size={20} />
                  </div>
                  <div className="admin-stat-content">
                    <span className="admin-stat-count">{count.toLocaleString()}</span>
                    <span className="admin-stat-label">{cat.label}</span>
                  </div>
                </button>
              );
            })}
          </div>

          {/* Main Content Area */}
          <div className="admin-content">
            {/* Content Header */}
            <div className="admin-content-header">
              <div className="admin-content-title-section">
                <div
                  className="admin-content-icon"
                  style={{ backgroundColor: category.bgColor, color: category.color }}
                >
                  <category.icon size={20} />
                </div>
                <div>
                  <h2 className="admin-content-title">{category.label}</h2>
                  <p className="admin-content-subtitle">
                    {currentData.length} records in {selectedEstablishmentName}
                  </p>
                </div>
              </div>
              <button
                onClick={handleRefresh}
                className="admin-refresh-btn"
                disabled={currentLoading}
              >
                <RefreshCw size={16} className={currentLoading ? 'spinning' : ''} />
                <span>Refresh</span>
              </button>
            </div>

            {/* Data Table */}
            <div className="admin-table-container">
              <DataTable
                columns={category.columns}
                data={currentData}
                onDelete={handleDeleteClick}
                loading={currentLoading}
                searchPlaceholder={category.searchPlaceholder}
                filters={filters}
                itemsPerPage={15}
              />
            </div>
          </div>
        </>
      ) : (
        /* Empty State - No Organization Selected */
        <div className="admin-empty-state">
          <div className="admin-empty-icon">
            <Building2 size={48} />
          </div>
          <h3 className="admin-empty-title">Select an Organization</h3>
          <p className="admin-empty-description">
            Choose an organization from the dropdown above to view and manage records.
          </p>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      <DeleteConfirmation
        isOpen={deleteModal.isOpen}
        onClose={() => setDeleteModal({ isOpen: false, item: null })}
        onConfirm={handleDeleteConfirm}
        itemName={deleteModal.item ? category.getItemName(deleteModal.item) : ''}
        itemType={category.itemType}
        loading={deleting}
      />
    </div>
  );
};

export default AdminDashboard;
